<template>
  <image-input-panel
      :is-generating="isGenerating"
      :progress="progress"
      @generate="generateImage"
      :wait-time="true"
      @update:generatorConfig="prompt = $event"
  />


  <div class="container">
    <div class="row">
      <div class="alert alert-warning" v-if="gallery.length >=12">
        You have reached limit of 12 images. The new generation will replace the oldest picture in the gallery.
        <a class="text-danger" href="/my-account">Create free account</a> to save unlimited pictures in organized collections.
      </div>
    </div>
    <image-gallery
        :gallery="gallery.length ? gallery : placeholderGallery"
        @select-image="selectImage"
    />
  </div>

  <ImagePreview
      ref="preview"
      :current-image="currentImage"
      :has-prev="hasPrevPicture"
      :has-next="hasNextPicture"
      @prev="handlePrevImage"
      @next="handleNextImage"
      @remove="handleRemove"

  />
</template>

<script>
import { ref, onMounted, useTemplateRef } from 'vue';
import AsyncApi from '@/services/AsyncApi';
import ImageInputPanel from '@/components/PromptBuilderLeftPanel.vue';
import ImagePreview from '@/components/ImagePreview.vue';
import GenerationProgress from '@/components/GenerationProgress.vue';
import ImageGallery from '@/components/Gallery.vue';
import AdRotator from "@/shared/AdsRotator.vue";
import {GALLERY_ENDPOINT} from "@/services/endpoints";

export default {
  name: 'ImageGenerator',

  components: {
    AdRotator,
    ImageInputPanel,
    ImagePreview,
    GenerationProgress,
    ImageGallery
  },

  setup() {

    const placeholderGallery = ref([
      'https://desirehub.com/wp-content/uploads/2025/01/placeholder.jpg',
      'https://desirehub.com/wp-content/uploads/2025/01/placeholder.jpg',
      'https://desirehub.com/wp-content/uploads/2025/01/placeholder.jpg',
      'https://desirehub.com/wp-content/uploads/2025/01/placeholder.jpg',
    ])
    const isPreviewActive = ref(false)
    const hasPrevPicture = ref(false)
    const hasNextPicture = ref(false)
    const currentWaitTime = ref(0)

    const checkHasNextPrev = () => {
      if(gallery.value.length <= 1){
        hasNextPicture.value = false;
        hasPrevPicture.value = false;
        return;
      }
      if(!currentImage.value){
        hasNextPicture.value = true;
        hasPrevPicture.value = true;
        return;
      }

      const index = gallery.value.indexOf(currentImage.value);
      if(index === 0){
        hasNextPicture.value = true;
        hasPrevPicture.value = false;
        return;
      }
      if(index >= gallery.value -1){
        hasNextPicture.value = false;
        hasPrevPicture.value = true;
        return;
      }

      hasNextPicture.value = true;
      hasPrevPicture.value = true;
    }
    
    const handlePrevImage  = async () => {
      if(gallery.value.length === 0){
        checkHasNextPrev();
        return;
      }

      if(!currentImage.value){
        currentImage.value =  gallery.value[0];
        checkHasNextPrev();
        return
      }

      const index = gallery.value.indexOf(currentImage.value);
      if(index - 1 >= 0){
        currentImage.value = gallery.value[index - 1];
      }
      checkHasNextPrev();
    }
    const handleNextImage = async () => {
      if(gallery.value.length === 0){
        checkHasNextPrev();
        return;
      }
      
      if(!currentImage.value){
        currentImage.value =  gallery.value[0];
        checkHasNextPrev();
        return
      }
      
      const index = gallery.value.indexOf(currentImage.value);
      if(index + 1 < gallery.value.length){
        currentImage.value = gallery.value[index + 1];
      }
      checkHasNextPrev();
    }

    const prompt = ref(null)
    const isGenerating = ref(false)
    const currentImage = ref(null)
    const gallery = ref([])
    const progress = ref(0)
    const ads = useTemplateRef('ads')

    const preview = useTemplateRef("preview");

    onMounted(() => {
      const savedGallery = localStorage.getItem('imageGallery')
      console.log("Mounting gallery");
      if (savedGallery) {
        gallery.value = JSON.parse(savedGallery)
      }

    })

    const generateImage = async () => {
      if (isGenerating.value){
        return;
      }

      isGenerating.value = true
      progress.value = 0
      currentWaitTime.value = parseInt(localStorage.getItem("waitTime")) ?? 0;

      if(currentWaitTime.value === 0){
        currentWaitTime.value = 10;
      } else {
        currentWaitTime.value += 5;
      }
      if(currentWaitTime.value > 35){
        currentWaitTime.value = 35;
      }
      localStorage.setItem("waitTime", `${currentWaitTime.value}`);

      // ads.value.rotateAd();

      try {
        const task = AsyncApi.scheduleTask("generate/image", prompt.value)

        for await (const progressData of task.progress()) {
          progress.value++
        }

        const result = await task
        const imageUrl = GALLERY_ENDPOINT + "/" + result['path'];

        gallery.value.unshift(imageUrl)
        if (gallery.value.length > 12) {
           gallery.value.splice(12);
        }

        localStorage.setItem('imageGallery', JSON.stringify(gallery.value))
        currentImage.value = imageUrl;
        hasNextPicture.value = false;
        hasPrevPicture.value = gallery.value.length > 1;

        selectImage(imageUrl);

      } catch (error) {
        console.error('Image generation failed:', error)
        alert('Failed to generate image. Please try again.')
      } finally {
        isGenerating.value = false
        progress.value = 0
      }
    }

    const handleRemove = () =>{
      const indexToRemove = gallery.value.indexOf(currentImage.value);

      gallery.value = gallery.value.filter(item => item !== currentImage.value);
      if(gallery.value.length) {
        if(gallery.value.length < indexToRemove){
          currentImage.value = gallery.value[indexToRemove];
        } else {
          currentImage.value = gallery.value[indexToRemove-1];
        }
      } else {
        currentImage.value = null;
      }
      
      localStorage.setItem('imageGallery', JSON.stringify(gallery.value))
    }

    const selectImage = (image) => {
      if(!gallery.value.length){
        return;
      }
      currentImage.value = image;
      checkHasNextPrev();
      preview.value.show();
    }

    return {
      prompt,
      isGenerating,
      currentImage,
      gallery,
      progress,
      placeholderGallery,
      generateImage,
      selectImage,
      isPreviewActive,
      hasPrevPicture,
      hasNextPicture,

      handlePrevImage,
      handleNextImage,
      handleRemove,
      currentWaitTime
    }
  }
}
</script>